import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from './cn';
import { useUiPortalContainer } from '@/js/wisemetering/ui/render_with_ui_root';
import { WM_FLOATING_SURFACE_CLASSNAME } from './surfaceStyles';
import { Text } from './Typography';

// Tooltip — short hover/focus hint anchored to a single trigger.
//
// Closed shape:
//
//   <Tooltip content="Delete this item" side="top">
//       <IconButton icon="trash" aria-label="Delete" />
//   </Tooltip>
//
// `content` is a string and renders inside `<Text variant="caption">`
// — the typographic commitment of the primitive. If a richer hint
// (icon + text, shortcut keys) shows up later, lift the constraint
// from a real callsite.
//
// `side` is the only positioning axis exposed. The other Radix axes
// (sideOffset / align / delayDuration) collapsed to baked defaults:
// 4px offset, center align, 150ms shared delay. The shared delay
// lives on the `<TooltipProvider>` mounted once in `ScopedUiRoot`,
// so adjacent tooltips skip the open delay (toolbar UX).

const TOOLTIP_CONTENT_ANIMATION_CLASSNAME = 'tw-animate-in tw-fade-in-0 tw-zoom-in-95 data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95 data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2';

const TooltipProvider = ({ delayDuration = 150, ...props }) => (
    <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
);

function Tooltip({ children, content, side = 'top' }) {
    const portalContainer = useUiPortalContainer();

    return (
        <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal container={portalContainer ?? undefined}>
                <TooltipPrimitive.Content
                    side={side}
                    sideOffset={4}
                    className={cn(
                        WM_FLOATING_SURFACE_CLASSNAME,
                        'tw-z-overlay tw-max-w-tooltip tw-overflow-hidden tw-rounded-xl tw-px-3 tw-py-2 tw-text-foreground',
                        TOOLTIP_CONTENT_ANIMATION_CLASSNAME
                    )}
                >
                    <Text tone="inherit" variant="caption">{content}</Text>
                </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
    );
}

export { Tooltip, TooltipProvider };
